/**
 * Bokeh depth of field with separated near / far fields, driven by a real
 * focus distance.
 *
 * The focal plane is metered off the G-buffer every frame (see FOCUS_FRAG) and
 * lives in a 1x1 texture the CoC shaders sample directly — never read back to
 * the CPU, because a readback is a pipeline stall and the value is only ever
 * needed by a shader anyway. Everything else is expressed as a *fraction* of
 * that distance, which is what makes one set of numbers work at 0.6 m on a
 * weapon and at 40 m down a street.
 *
 * Why the frame needs this at all: three of the four best-scoring shipped Call
 * of Duty frames in `refs/` use depth of field as a primary compositional tool
 * — a soft foreground occluder, a sharp subject, a soft background — and every
 * frame this build produced had one flat focal plane from the muzzle to the
 * skybox. The previous fix for a real bug (the sky arriving as viewZ == 0 and
 * being read as the *near* field, which erased every cable and awning in shot)
 * pushed the hip-fire far field out to 800 m, which removed the artefact by
 * removing the effect. The bug is fixed properly here — background pixels take
 * `uBackgroundZ` — so the far field can come back.
 *
 * The viewmodel is identified through the G-buffer flag channel so the two
 * fields never bleed into each other.
 *
 * owner: render
 */
import * as THREE from 'three';
import { Blitter, makeMaterial, makeRT, GLSL_COMMON } from './Common.js';

const COC_GLSL = /* glsl */`
uniform sampler2D tFocus;   // 1x1: r = smoothed focus distance in metres
uniform float uFocusOverride; // > 0 pins the focal plane (a shot asked for it)
uniform float uNearFrac;    // near blur starts below focus * uNearFrac
uniform float uNearRoll;    // ramp width, as a fraction of that start distance
uniform float uFarFrac;     // far blur starts beyond focus * uFarFrac
uniform float uFarRoll;
uniform float uVmCoc;
uniform float uBackgroundZ;  // stand-in distance for pixels the prepass never wrote

float pfFocusDistance() {
  if (uFocusOverride > 0.0) return uFocusOverride;
  return max(texture2D(tFocus, vec2(0.5)).r, 0.05);
}

/* signed CoC in [-1,1]; negative = in front of the focus plane (near field).
   Game DOF, not a thin lens: an explicit in-focus band around the focal plane
   keeps the subject and everything a player needs to read crisp, which a pure
   1/z falloff never does. */
float computeCoc(float viewZ, float flag) {
  if (flag > 0.5) return clamp(uVmCoc, -1.0, 1.0);
  /* The prepass clears gData to zero and never writes the sky, so background
     pixels arrive with viewZ == 0. max(viewZ, 0.02) used to read that as
     "2 cm from the lens", i.e. the deepest possible NEAR field — and the near
     field is explicitly allowed to spill over everything behind it. The result
     was that every square millimetre of visible sky sprayed a full-radius near
     bokeh over its neighbours and erased every thin object silhouetted against
     it: overhead cables vanished completely, awning panels eroded from 140 px
     to a 20 px core. The sky is at infinity, not against the lens. */
  float z = viewZ > 1e-4 ? viewZ : uBackgroundZ;
  float S = pfFocusDistance();
  float nearStart = S * uNearFrac;
  float nearRange = max(nearStart * uNearRoll, 1e-3);
  float farStart = S * uFarFrac;
  float farRange = max(farStart * uFarRoll, 1e-3);
  float far = clamp((z - farStart) / farRange, 0.0, 1.0);
  float near = clamp((nearStart - z) / nearRange, 0.0, 1.0);
  far = far * far * (3.0 - 2.0 * far);
  near = near * near * (3.0 - 2.0 * near);
  return far > near ? far : -near;
}
`;

/**
 * Centre-weighted autofocus.
 *
 * A *harmonic* mean over the metering box, not an arithmetic one: averaging
 * distances lets one patch of far wall in the corner of the box drag the focal
 * plane off a subject that fills the middle of it, while averaging 1/z does
 * what a camera does and weights the near subject. Sky (viewZ == 0) and the
 * viewmodel (flag == 1) are both excluded — neither is ever what the frame is
 * about, and the weapon in particular sits 40 cm from the lens and would pin
 * the focus there in every single shot.
 */
const FOCUS_FRAG = /* glsl */`
precision highp float;
${GLSL_COMMON}
uniform sampler2D tData;
uniform sampler2D tPrev;
uniform vec2 uCenter;
uniform float uSpread;
uniform float uAspect;
uniform float uDt;
uniform float uSpeed;
uniform float uSnap;
uniform float uMin;
uniform float uMax;
varying vec2 vUv;

void main() {
  float wsum = 0.0, isum = 0.0;
  for (int y = -2; y <= 2; y++) {
    for (int x = -2; x <= 2; x++) {
      vec2 o = vec2(float(x) / uAspect, float(y)) * (uSpread * 0.5);
      vec4 d = texture2D(tData, clamp(uCenter + o, vec2(0.002), vec2(0.998)));
      float z = d.b;
      if (z <= 1e-4) continue;
      if (d.a > 0.5) continue;
      float w = exp(-float(x * x + y * y) * 0.32);
      isum += w / max(z, 0.2);
      wsum += w;
    }
  }
  float target = (wsum > 1e-4 && isum > 1e-6) ? (wsum / isum) : uMax;
  target = clamp(target, uMin, uMax);

  float prev = texture2D(tPrev, vec2(0.5)).r;
  if (!(prev > 0.0) || uSnap > 0.5) prev = target;
  float f = mix(prev, target, 1.0 - exp(-max(uDt, 0.0) * uSpeed));
  gl_FragColor = vec4(f, target, 0.0, 1.0);
}
`;

const PREFILTER_FRAG = /* glsl */`
precision highp float;
${GLSL_COMMON}
${COC_GLSL}
uniform sampler2D tColor;
uniform sampler2D tData;
uniform vec2 uSrcTexel;
varying vec2 vUv;

void main() {
  vec2 o = uSrcTexel * 0.5;
  vec3 c = vec3(0.0);
  float best = 0.0;
  float coc = 0.0;
  for (int i = 0; i < 4; i++) {
    vec2 d = vec2(i == 0 || i == 2 ? -o.x : o.x, i < 2 ? -o.y : o.y);
    vec2 uv = vUv + d;
    vec3 s = max(texture2D(tColor, uv).rgb, vec3(0.0));
    c += s;
    vec4 dta = texture2D(tData, uv);
    float sc = computeCoc(dta.b, dta.a);
    if (abs(sc) > best) { best = abs(sc); coc = sc; }
  }
  // tame fireflies before they get smeared into a big bokeh disc
  c *= 0.25;
  float l = maxc(c);
  if (l > 12.0) c *= 12.0 / l;
  gl_FragColor = vec4(c, coc);
}
`;

const GATHER_FRAG = /* glsl */`
precision highp float;
${GLSL_COMMON}
layout(location = 0) out vec4 outFar;
layout(location = 1) out vec4 outNear;

uniform sampler2D tSrc;      // rgb color, a signed coc
uniform sampler2D tNoise;
uniform vec2 uTexel;
uniform vec2 uNoiseScale;
uniform float uMaxCocPx;
uniform float uFrame;
uniform float uHexBlend;
varying vec2 vUv;

void main() {
  vec4 center = texture2D(tSrc, vUv);
  float centerCoc = center.a;

  vec4 bn = texture2D(tNoise, vUv * uNoiseScale);
  float rot = fract(bn.r + uFrame * 0.6180339887498949) * 6.2831853;

  float radius = max(abs(centerCoc), 0.0) * uMaxCocPx;

  vec3 farSum = center.rgb * max(centerCoc, 0.0);
  float farW = max(centerCoc, 0.0);
  vec3 nearSum = center.rgb * max(-centerCoc, 0.0);
  float nearW = max(-centerCoc, 0.0);

  for (int i = 0; i < PF_DOF_TAPS; i++) {
    float fi = (float(i) + 0.5) / float(PF_DOF_TAPS);
    float ang = float(i) * 2.39996323 + rot;
    float rr = sqrt(fi);

    // hexagonal iris: push the unit disc out to a hexagon boundary
    float ha = mod(ang, 1.0471975512) - 0.5235987756;
    float hex = 0.8660254038 / cos(ha);
    rr *= mix(1.0, hex, uHexBlend);

    vec2 dir = vec2(cos(ang), sin(ang)) * rr;
    float distPx = length(dir) * uMaxCocPx;
    vec2 uv = vUv + dir * uMaxCocPx * uTexel;

    vec4 s = texture2D(tSrc, uv);
    float sCoc = s.a;
    float sRad = abs(sCoc) * uMaxCocPx;

    // a sample only contributes where its own disc actually reaches us
    float cover = saturate(sRad - distPx + 1.0);

    // far field: never let a sharp foreground sample leak backwards
    float wf = cover * step(0.0, sCoc) * step(distPx - 1.0, max(radius, sRad));
    farSum += s.rgb * wf;
    farW += wf;

    // near field: allowed to spill over everything behind it
    float wn = cover * step(sCoc, 0.0) * max(-sCoc, 0.0);
    nearSum += s.rgb * wn;
    nearW += wn;
  }

  outFar = vec4(farSum / max(farW, 1e-4), saturate(farW > 0.0 ? max(centerCoc, 0.0) : 0.0));
  outNear = vec4(nearSum / max(nearW, 1e-4), saturate(nearW * (2.0 / float(PF_DOF_TAPS)) * 3.0));
}
`;

const COMPOSITE_FRAG = /* glsl */`
precision highp float;
${GLSL_COMMON}
${COC_GLSL}
uniform sampler2D tColor;
uniform sampler2D tFar;
uniform sampler2D tNear;
uniform sampler2D tData;
varying vec2 vUv;

void main() {
  vec3 sharp = max(texture2D(tColor, vUv).rgb, vec3(0.0));
  vec4 dta = texture2D(tData, vUv);
  float coc = computeCoc(dta.b, dta.a);

  vec4 far = texture2D(tFar, vUv);
  vec4 near = texture2D(tNear, vUv);

  float fw = smoothstep(0.015, 0.30, max(coc, 0.0));
  vec3 c = mix(sharp, far.rgb, fw);

  float nw = saturate(near.a);
  c = mix(c, near.rgb, nw);

  gl_FragColor = vec4(c, 1.0);
}
`;

export class DOF {
  constructor(blitter) {
    this.blit = blitter || new Blitter();
    this.halfRT = null;
    this.fieldRT = null;
    this.outRT = null;
    this.w = 0; this.h = 0;
    this.aspect = 16 / 9;
    // 32 bit: the focal plane is a distance in metres, and half float quantises
    // 40 m into steps you can see the bokeh jump across.
    const focusOpts = {
      type: THREE.FloatType, minFilter: THREE.NearestFilter, magFilter: THREE.NearestFilter,
    };
    this.focusA = makeRT(1, 1, { ...focusOpts, name: 'postfx.dof.focus.a' });
    this.focusB = makeRT(1, 1, { ...focusOpts, name: 'postfx.dof.focus.b' });
    this.ping = 0;
    this.snap = true;

    this.matFocus = makeMaterial({
      name: 'postfx.dof.focus',
      fragmentShader: FOCUS_FRAG,
      uniforms: {
        tData: { value: null }, tPrev: { value: null },
        uCenter: { value: new THREE.Vector2(0.5, 0.5) },
        uSpread: { value: 0.22 },
        uAspect: { value: 16 / 9 },
        uDt: { value: 1 / 60 },
        uSpeed: { value: 4.5 },
        uSnap: { value: 1 },
        uMin: { value: 1.2 },
        uMax: { value: 60.0 },
      },
    });

    const cocUniforms = () => ({
      tFocus: { value: null },
      uFocusOverride: { value: 0.0 },
      uNearFrac: { value: 0.30 },
      uNearRoll: { value: 0.90 },
      uFarFrac: { value: 2.60 },
      uFarRoll: { value: 4.00 },
      uVmCoc: { value: 0.0 },
      /* The sky dome sits ~1.2 km out. Feeding the far ramp a real distance
         rather than 0 is what stops the sky being read as the near field. */
      uBackgroundZ: { value: 1000.0 },
    });

    this.matPre = makeMaterial({
      name: 'postfx.dof.prefilter',
      fragmentShader: PREFILTER_FRAG,
      uniforms: Object.assign({
        tColor: { value: null }, tData: { value: null },
        uSrcTexel: { value: new THREE.Vector2() },
      }, cocUniforms()),
    });

    this.matGather = makeMaterial({
      name: 'postfx.dof.gather',
      glsl3: true,
      fragmentShader: GATHER_FRAG,
      defines: { PF_DOF_TAPS: 32 },
      uniforms: {
        tSrc: { value: null }, tNoise: { value: null },
        uTexel: { value: new THREE.Vector2() },
        uNoiseScale: { value: new THREE.Vector2() },
        uMaxCocPx: { value: 14.0 },
        uFrame: { value: 0 },
        uHexBlend: { value: 0.35 },
      },
    });

    this.matComposite = makeMaterial({
      name: 'postfx.dof.composite',
      fragmentShader: COMPOSITE_FRAG,
      uniforms: Object.assign({
        tColor: { value: null }, tFar: { value: null }, tNear: { value: null }, tData: { value: null },
      }, cocUniforms()),
    });
  }

  setQuality(taps) {
    if (this.matGather.defines.PF_DOF_TAPS === taps) return;
    this.matGather.defines.PF_DOF_TAPS = taps;
    this.matGather.needsUpdate = true;
  }

  setSize(w, h) {
    if (this.w === w && this.h === h) return;
    this.w = w; this.h = h;
    const hw = Math.max(1, w >> 1), hh = Math.max(1, h >> 1);
    for (const rt of [this.halfRT, this.fieldRT, this.outRT]) if (rt) rt.dispose();
    this.halfRT = makeRT(hw, hh, { name: 'postfx.dof.half' });
    this.fieldRT = makeRT(hw, hh, { count: 2, name: 'postfx.dof.fields' });
    this.outRT = makeRT(w, h, { name: 'postfx.dof.out' });
    this.aspect = w / Math.max(1, h);
  }

  requestSnap() { this.snap = true; }

  /** Metered focal plane in metres, for tools and reports. Never used to drive the shader. */
  readFocus(renderer) {
    const buf = new Float32Array(4);
    renderer.readRenderTargetPixels(this.ping ? this.focusB : this.focusA, 0, 0, 1, 1, buf);
    return { smoothed: buf[0], target: buf[1] };
  }

  _applyCoc(mat, p) {
    const u = mat.uniforms;
    u.tFocus.value = (this.ping ? this.focusB : this.focusA).texture;
    u.uFocusOverride.value = p.focusOverride > 0 ? p.focusOverride : 0;
    u.uNearFrac.value = p.nearFrac;
    u.uNearRoll.value = p.nearRoll;
    u.uFarFrac.value = p.farFrac;
    u.uFarRoll.value = p.farRoll;
    u.uVmCoc.value = p.vmCoc;
  }

  /** Meter the focal plane. Runs before the CoC is evaluated anywhere. */
  _focus(renderer, p) {
    const src = this.ping ? this.focusB : this.focusA;
    const dst = this.ping ? this.focusA : this.focusB;
    this.ping ^= 1;
    const u = this.matFocus.uniforms;
    u.tData.value = p.dataTexture;
    u.tPrev.value = src.texture;
    u.uAspect.value = this.aspect;
    u.uDt.value = p.dt || 1 / 60;
    u.uSnap.value = (this.snap || p.snap) ? 1 : 0;
    u.uSpread.value = p.focusSpread ?? 0.22;
    u.uMin.value = p.focusMin ?? 1.2;
    u.uMax.value = p.focusMax ?? 60.0;
    this.blit.draw(renderer, this.matFocus, dst);
    this.snap = false;
  }

  render(renderer, p) {
    this._focus(renderer, p);
    this._applyCoc(this.matPre, p);
    this._applyCoc(this.matComposite, p);

    const pre = this.matPre.uniforms;
    pre.tColor.value = p.colorTexture;
    pre.tData.value = p.dataTexture;
    pre.uSrcTexel.value.set(1 / this.w, 1 / this.h);
    this.blit.draw(renderer, this.matPre, this.halfRT);

    const g = this.matGather.uniforms;
    g.tSrc.value = this.halfRT.texture;
    g.tNoise.value = p.noiseTexture;
    g.uTexel.value.set(1 / this.halfRT.width, 1 / this.halfRT.height);
    g.uNoiseScale.value.set(this.halfRT.width / (p.noiseSize || 64), this.halfRT.height / (p.noiseSize || 64));
    g.uMaxCocPx.value = p.maxCocPixels;
    g.uFrame.value = p.frame % 64;
    this.blit.draw(renderer, this.matGather, this.fieldRT);

    const c = this.matComposite.uniforms;
    c.tColor.value = p.colorTexture;
    c.tFar.value = this.fieldRT.textures[0];
    c.tNear.value = this.fieldRT.textures[1];
    c.tData.value = p.dataTexture;
    this.blit.draw(renderer, this.matComposite, this.outRT);

    return this.outRT.texture;
  }

  dispose() {
    for (const rt of [this.halfRT, this.fieldRT, this.outRT, this.focusA, this.focusB]) if (rt) rt.dispose();
    this.matPre.dispose(); this.matGather.dispose(); this.matComposite.dispose();
    this.matFocus.dispose();
  }
}
